import React from "react";
import { useTranslation } from "react-i18next";
import { User } from "lucide-react";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import type { StaffOption } from "../utils/types";

type StaffAssignmentPanelProps = {
  staffId: string;
  onStaffIdChange: (id: string) => void;
  fallback: string;
  onFallbackChange: (value: string) => void;
  staffOptions: StaffOption[];
  selectedStaff?: StaffOption;
};

export function StaffAssignmentPanel({
  staffId,
  onStaffIdChange,
  fallback,
  onFallbackChange,
  staffOptions,
  selectedStaff,
}: StaffAssignmentPanelProps) {
  const { t } = useTranslation();

  return (
    <div className="border-border bg-muted/30 rounded-xl border p-4 md:p-5">
      <div className="grid gap-6 md:grid-cols-[1fr,minmax(220px,280px)] md:items-start">
        <div className="space-y-5">
          <div className="space-y-2">
            <Label htmlFor="staff-member">
              {t("layout.messagingSettingsPage.staffMember")}
            </Label>
            <Select
              value={staffId || undefined}
              onValueChange={onStaffIdChange}
              disabled={staffOptions.length === 0}
            >
              <SelectTrigger
                id="staff-member"
                size="md"
                className="border-base-border h-12 w-full min-w-0 justify-between bg-white"
              >
                <SelectValue
                  placeholder={t(
                    "layout.messagingSettingsPage.emptyPlaceholder",
                  )}
                />
              </SelectTrigger>
              <SelectContent>
                {staffOptions.length === 0 ? (
                  <SelectItem value="no-staff" disabled>
                    {t("layout.messagingSettingsPage.emptyPlaceholder")}
                  </SelectItem>
                ) : (
                  staffOptions.map((s) => (
                    <SelectItem key={s.id} value={s.id} textValue={s.name}>
                      <span className="flex items-center gap-2">
                        <User className="text-muted-foreground size-4" />
                        {s.name}
                      </span>
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="fallback">
              {t("layout.messagingSettingsPage.fallbackBehavior")}
            </Label>
            <Select value={fallback} onValueChange={onFallbackChange}>
              <SelectTrigger
                id="fallback"
                size="md"
                className="border-base-border h-12 w-full min-w-0 justify-between bg-white"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="hotel_default">
                  {t("layout.messagingSettingsPage.fallbackUseHotelDefault")}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="border-border bg-card rounded-xl border p-4 shadow-xs">
          <div className="flex gap-3">
            <div className="relative shrink-0">
              <Avatar className="size-14 text-base">
                <AvatarFallback className="bg-slate-200 text-slate-800">
                  {selectedStaff?.initials ?? "—"}
                </AvatarFallback>
              </Avatar>
              <span
                className="border-card bg-skyline-2 absolute right-0 bottom-0 size-3 rounded-full border-2"
                title={t("layout.messagingSettingsPage.staffOnlineStatus")}
              />
            </div>
            <div className="min-w-0 flex-1 space-y-0.5">
              <p className="text-foreground truncate font-semibold">
                {selectedStaff?.name ??
                  t("layout.messagingSettingsPage.emptyPlaceholder")}
              </p>
              <p className="text-muted-foreground text-sm">
                {selectedStaff?.role ?? "—"}
              </p>
              <p className="text-foreground/90 text-sm">
                {selectedStaff?.phone ?? "—"}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
